import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useLiveEvents } from '../lib/useWs.js';

interface Subscription {
  tier: 'trial' | 'starter' | 'pro' | 'expired';
  paid_until: number;
  days_remaining: number;
  interval_seconds: number;
  next_tick_at: number;
}

interface DecisionRow {
  id: string;
  asset: string;
  action: string;
  side: string | null;
  rationale: string;
  confidence: number;
  tick_at: number;
  validation_ok: 0 | 1;
}

interface TradeRow {
  id: string;
  mode: 'paper' | 'live';
  asset: string;
  side: string;
  size: number;
  leverage: number;
  entry_price: number | null;
  exit_price: number | null;
  pnl_usd: number | null;
  status: string;
  opened_at: number;
  closed_at: number | null;
}

interface HistoryResponse {
  decisions: DecisionRow[];
  trades: TradeRow[];
}

interface TickSummaryPayload {
  tick_id?: string;
  decision_id?: string;
  decisions?: number;
  trades?: number;
  rejected?: number;
  mode?: string;
  source?: string;
  scheduled_at?: number;
  assets?: string[];
}

const liveTickIcon: Record<'connecting' | 'open' | 'closed' | 'error', string> = {
  connecting: 'Connecting',
  open: 'Live',
  closed: 'Offline',
  error: 'Error',
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const tickSummary = (payload: unknown): TickSummaryPayload => {
  if (!isRecord(payload)) return {};
  const out: TickSummaryPayload = {};
  if (typeof payload.tick_id === 'string') out.tick_id = payload.tick_id;
  if (typeof payload.decision_id === 'string') out.decision_id = payload.decision_id;
  if (typeof payload.decisions === 'number') out.decisions = payload.decisions;
  if (typeof payload.trades === 'number') out.trades = payload.trades;
  if (typeof payload.rejected === 'number') out.rejected = payload.rejected;
  if (typeof payload.mode === 'string') out.mode = payload.mode;
  if (typeof payload.source === 'string') out.source = payload.source;
  if (typeof payload.scheduled_at === 'number') out.scheduled_at = payload.scheduled_at;
  if (Array.isArray(payload.assets) && payload.assets.every((v) => typeof v === 'string')) {
    out.assets = payload.assets;
  }
  return out;
};

const shortId = (id?: string): string => (id ? `${id.slice(0, 6)}…${id.slice(-4)}` : '—');

export const Dashboard = () => {
  const { me, signOut } = useAuth();
  const sub = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<Subscription>('/me/subscription'),
  });
  const recent = useQuery({
    queryKey: ['recent-decisions'],
    queryFn: () => api.get<HistoryResponse>('/me/history?limit=20'),
    refetchInterval: 30_000,
  });

  const { events, state: wsState } = useLiveEvents(20);
  const latestTick = useMemo(() => {
    const evt = events
      .slice()
      .reverse()
      .find((event) =>
        ['tick_queued', 'tick_started', 'tick_summary'].includes(event.payload.kind),
      );
    return evt ? { event: evt, summary: tickSummary(evt.payload.payload) } : null;
  }, [events]);
  const latestTickState =
    latestTick?.event.payload.kind === 'tick_queued'
      ? 'Queued'
      : latestTick?.event.payload.kind === 'tick_started'
        ? 'Running'
        : latestTick?.event.payload.kind === 'tick_summary'
          ? 'Processed'
          : 'Idle';
  const nextTickLabel = sub.data
    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
        Math.round((sub.data.next_tick_at - Date.now()) / 60_000),
        'minute',
      )
    : 'Loading';
  const openPositions = useMemo(
    () => (recent.data?.trades ?? []).filter((t) => t.status === 'open'),
    [recent.data?.trades],
  );
  const pastPositions = useMemo(
    () => (recent.data?.trades ?? []).filter((t) => t.status !== 'open'),
    [recent.data?.trades],
  );

  // Build a synthetic equity curve from the trades. Each closed trade contributes its pnl.
  const equityPoints = useMemo(() => {
    const trades = (recent.data?.trades ?? []).slice().sort((a, b) => a.opened_at - b.opened_at);
    let equity = 10_000; // matches paper-equity starting value in UserState DO
    const points = [{ t: trades[0]?.opened_at ?? Date.now() - 86_400_000, equity }];
    for (const t of trades) {
      if (t.pnl_usd != null) equity += t.pnl_usd;
      points.push({ t: t.closed_at ?? t.opened_at, equity });
    }
    return points;
  }, [recent.data?.trades]);

  return (
    <main className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">Trading Command Center</p>
          <h1>Live operations for your paper agent.</h1>
          <p className="lead">
            See whether the agent is connected, when it will run next, and what it decided after
            each market tick.
          </p>
          <span className="wallet-chip">{me?.wallet_address}</span>
        </div>
        <div className="stack">
          <div className="card panel-strong">
            <div className="row">
              <div>
                <span className="label">System status</span>
                <p className="stat-value">{liveTickIcon[wsState]}</p>
              </div>
              <span className={`badge ${wsState === 'open' ? 'badge-success' : ''}`}>
                WebSocket
              </span>
            </div>
          </div>
          <button className="btn btn-danger" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <section className="grid grid-3" style={{ marginTop: '1rem' }}>
        <div className="card stat">
          <span className="label">Subscription</span>
          <p className="stat-value">{sub.data?.tier ?? '—'}</p>
          <p className="muted">
            {sub.data ? `${sub.data.days_remaining} days remaining` : 'Loading…'}
          </p>
        </div>
        <div className="card stat">
          <span className="label">Tick interval</span>
          <p className="stat-value">{sub.data ? `${sub.data.interval_seconds / 60}m` : '—'}</p>
          <p className="muted">Next run {nextTickLabel}</p>
        </div>
        <div className="card stat">
          <span className="label">Recent decisions</span>
          <p className="stat-value">{recent.data?.decisions.length ?? '—'}</p>
          <p className="muted">Last 20 model outputs</p>
        </div>
      </section>

      <section className="grid grid-2" style={{ marginTop: '1rem' }}>
        <div className="panel">
          <div className="section-header">
            <div>
              <h2>Open positions</h2>
              <p className="muted" style={{ marginBottom: 0 }}>
                Current paper/live exposure. There should only be one open row per asset.
              </p>
            </div>
            <span className="badge badge-warning">{openPositions.length} open</span>
          </div>
          {openPositions.length === 0 ? (
            <div className="empty-state">
              <div>
                <strong>No open positions</strong>
                <p>The agent has no current exposure.</p>
              </div>
            </div>
          ) : (
            openPositions.map((trade) => <TradeCard key={trade.id} trade={trade} />)
          )}
        </div>

        <div className="panel">
          <div className="section-header">
            <div>
              <h2>Past positions</h2>
              <p className="muted" style={{ marginBottom: 0 }}>
                Closed trades with realized PnL.
              </p>
            </div>
            <span className="badge">{pastPositions.length} closed</span>
          </div>
          {pastPositions.length === 0 ? (
            <div className="empty-state">
              <div>
                <strong>No closed positions yet</strong>
                <p>Closed positions will appear here after stop, take-profit, or AI exits.</p>
              </div>
            </div>
          ) : (
            pastPositions.slice(0, 10).map((trade) => <TradeCard key={trade.id} trade={trade} />)
          )}
        </div>
      </section>

      {latestTick && (
        <section className="panel panel-strong" style={{ marginTop: '1rem' }}>
          <div className="section-header">
            <div>
              <span className="label">Latest tick</span>
              <h2 style={{ marginBottom: 0 }}>
                {latestTickState === 'Queued'
                  ? 'AI tick queued'
                  : latestTickState === 'Running'
                    ? 'AI tick running'
                    : latestTick.summary.trades
                      ? `${latestTick.summary.trades} paper trade${latestTick.summary.trades === 1 ? '' : 's'}`
                      : 'Decision completed, no trade opened'}
              </h2>
              <p className="muted" style={{ marginBottom: 0 }}>
                {new Date(latestTick.event.ts).toLocaleString()} ·{' '}
                {latestTick.summary.mode ?? 'paper'}
              </p>
            </div>
            <span
              className={`badge ${
                latestTickState === 'Processed' ? 'badge-success' : 'badge-warning'
              }`}
            >
              {latestTickState}
            </span>
          </div>
          <div className="grid grid-3" style={{ marginTop: '1rem' }}>
            <div className="card">
              <span className="label">Decisions</span>
              <p className="stat-value">{latestTick.summary.decisions ?? 0}</p>
            </div>
            <div className="card">
              <span className="label">Trades</span>
              <p className="stat-value">{latestTick.summary.trades ?? 0}</p>
            </div>
            <div className="card">
              <span className="label">Rejected</span>
              <p className="stat-value">{latestTick.summary.rejected ?? 0}</p>
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'flex-start', marginTop: '1rem' }}>
            <span className="badge">Tick {shortId(latestTick.summary.tick_id)}</span>
            <span className="badge">Decision {shortId(latestTick.summary.decision_id)}</span>
          </div>
        </section>
      )}

      <section className="panel" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h2>Equity curve</h2>
          <span className="badge">Paper account</span>
        </div>
        {sub.isLoading && <p>Loading…</p>}
        {equityPoints.length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={equityPoints}>
              <XAxis dataKey="t" tickFormatter={(t) => new Date(t).toLocaleDateString()} />
              <YAxis domain={['dataMin - 100', 'dataMax + 100']} />
              <Tooltip
                labelFormatter={(t) => new Date(Number(t)).toLocaleString()}
                formatter={(v) => `$${Number(v).toFixed(2)}`}
              />
              <Line type="monotone" dataKey="equity" stroke="#2dd4bf" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-state">
            <div>
              <strong>No equity curve yet</strong>
              <p>Start paper trading from Settings and the chart will populate after fills.</p>
            </div>
          </div>
        )}
      </section>

      <section className="grid grid-2" style={{ marginTop: '1rem' }}>
        <div className="panel">
          <div className="section-header">
            <h2>Live events</h2>
            <span className="badge">{events.length}</span>
          </div>
          {events.length === 0 ? (
            <div className="empty-state">
              <div>
                <strong>Waiting for live events</strong>
                <p>Decision, trade, and notification events will stream here.</p>
              </div>
            </div>
          ) : (
            <div className="event-list">
              {events
                .slice()
                .reverse()
                .map((e) => (
                  <EventCard key={e.id} event={e} />
                ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="section-header">
            <h2>Recent AI decisions</h2>
            <span className="badge">AI Gateway</span>
          </div>
          {recent.isLoading && <p>Loading…</p>}
          {recent.data?.decisions.length === 0 && (
            <div className="empty-state">
              <div>
                <strong>No AI decisions yet</strong>
                <p>Use Start paper trading to schedule the first model run.</p>
              </div>
            </div>
          )}
          {recent.data?.decisions.map((d) => (
            <DecisionCard key={`${d.id}-${d.asset}`} decision={d} />
          ))}
        </div>
      </section>
    </main>
  );
};

const EventCard = ({ event }: { event: ReturnType<typeof useLiveEvents>['events'][number] }) => {
  const summary = tickSummary(event.payload.payload);
  const title =
    event.payload.kind === 'tick_summary'
      ? summary.trades
        ? `${summary.trades} trade${summary.trades === 1 ? '' : 's'} opened`
        : 'AI tick completed'
      : event.payload.kind === 'tick_queued'
        ? 'AI tick queued'
        : event.payload.kind === 'tick_started'
          ? 'AI tick running'
          : event.payload.kind.replaceAll('_', ' ');

  return (
    <article className="event-card">
      <div className="event-dot" />
      <div>
        <div className="section-header">
          <strong>{title}</strong>
          <small className="muted">{new Date(event.ts).toLocaleTimeString()}</small>
        </div>
        {['tick_queued', 'tick_started', 'tick_summary'].includes(event.payload.kind) ? (
          <>
            <div className="event-metrics">
              {event.payload.kind === 'tick_queued' && <span>waiting in queue</span>}
              {event.payload.kind === 'tick_started' && <span>fetching market + AI</span>}
              {event.payload.kind === 'tick_summary' && (
                <>
                  <span>{summary.decisions ?? 0} decisions</span>
                  <span>{summary.trades ?? 0} trades</span>
                  <span>{summary.rejected ?? 0} rejected</span>
                </>
              )}
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>
              Tick {shortId(summary.tick_id)} · Decision {shortId(summary.decision_id)}
            </p>
          </>
        ) : (
          <p className="muted" style={{ marginBottom: 0 }}>
            {JSON.stringify(event.payload.payload)}
          </p>
        )}
      </div>
    </article>
  );
};

const formatUsd = (v: number | null): string => (v == null ? '—' : `$${v.toFixed(2)}`);
const formatPrice = (v: number | null): string => (v == null ? '—' : `$${v.toLocaleString()}`);

const TradeCard = ({ trade }: { trade: TradeRow }) => {
  const isOpen = trade.status === 'open';
  const pnlClass =
    trade.pnl_usd == null ? '' : trade.pnl_usd >= 0 ? 'badge-success' : 'badge-danger';
  return (
    <article className="decision-card">
      <div className="section-header">
        <div>
          <strong>
            {trade.asset} {trade.side}
          </strong>{' '}
          <span className={`badge ${isOpen ? 'badge-warning' : pnlClass}`}>
            {trade.status} · {trade.mode}
          </span>
        </div>
        <small className="muted">{new Date(trade.opened_at).toLocaleString()}</small>
      </div>
      <div className="event-metrics">
        <span>Entry {formatPrice(trade.entry_price)}</span>
        <span>Exit {formatPrice(trade.exit_price)}</span>
        <span>Size {trade.size.toFixed(6)}</span>
        <span>{trade.leverage}x</span>
        <span>PnL {formatUsd(trade.pnl_usd)}</span>
      </div>
      {trade.closed_at && (
        <p className="muted" style={{ marginBottom: 0 }}>
          Closed {new Date(trade.closed_at).toLocaleString()}
        </p>
      )}
    </article>
  );
};

const DecisionCard = ({ decision }: { decision: DecisionRow }) => (
  <article className="decision-card">
    <div className="section-header">
      <div>
        <strong>{decision.asset}</strong>{' '}
        <span className="badge">
          {decision.action}
          {decision.side ? ` · ${decision.side}` : ''}
        </span>
      </div>
      <span className={`badge ${decision.validation_ok ? 'badge-success' : 'badge-warning'}`}>
        {decision.validation_ok ? 'valid' : 'fallback'}
      </span>
    </div>
    <p className="muted" style={{ margin: '0.65rem 0' }}>
      {decision.rationale}
    </p>
    <div className="event-metrics">
      <span>
        {decision.validation_ok
          ? `Confidence ${(decision.confidence * 100).toFixed(0)}%`
          : 'Confidence n/a'}
      </span>
      <span>{new Date(decision.tick_at).toLocaleTimeString()}</span>
    </div>
  </article>
);
